import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Plus } from "lucide-react";

import {
  type EnergyType,
  type TariffCreate,
  type TariffType,
  clientsApi,
} from "@/api/clients";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";

interface TariffsSectionProps {
  clientId: string;
  energyType: EnergyType;
  tariffType: TariffType;
  canEdit: boolean;
}

export function TariffsSection({ clientId, energyType, tariffType, canEdit }: TariffsSectionProps) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [showAdd, setShowAdd] = useState(false);

  const tariffs = useQuery({
    queryKey: ["client-tariffs", clientId, energyType],
    queryFn: () => clientsApi.listTariffs(clientId, energyType),
  });

  const isHeat = energyType === "heat";
  const unitLabel = t(`clients.tariff.fixed_price_unit_${energyType}`);

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle>{t("clients.tariffs")}</CardTitle>
          {canEdit && (
            <Button size="sm" onClick={() => setShowAdd(true)}>
              <Plus className="h-4 w-4" /> {t("clients.add_tariff")}
            </Button>
          )}
        </div>
      </CardHeader>
      <CardContent>
        {tariffs.data && tariffs.data.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-slate-500">
                  <th className="p-2">{t("clients.tariff.effective_from")}</th>
                  <th className="p-2">{t("clients.tariff.fixed_price")}</th>
                  <th className="p-2">{t("clients.tariff.energy_type")}</th>
                </tr>
              </thead>
              <tbody>
                {tariffs.data.map((tr) => (
                  <tr key={tr.id} className="border-t border-slate-100">
                    <td className="p-2 font-mono text-sm">{tr.effective_from}</td>
                    <td className="p-2">
                      {tr.fixed_price !== null ? `${tr.fixed_price} ${unitLabel}` : "—"}
                    </td>
                    <td className="p-2 text-xs text-slate-500">{tr.energy_type}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="text-sm text-slate-500">{t("clients.no_tariffs")}</div>
        )}

        {showAdd && (
          <AddTariffModal
            clientId={clientId}
            energyType={energyType}
            tariffType={tariffType}
            isHeat={isHeat}
            onClose={() => setShowAdd(false)}
            onCreated={() => {
              setShowAdd(false);
              void qc.invalidateQueries({ queryKey: ["client-tariffs", clientId, energyType] });
            }}
          />
        )}
      </CardContent>
    </Card>
  );
}

function AddTariffModal({
  clientId,
  energyType,
  tariffType,
  isHeat,
  onClose,
  onCreated,
}: {
  clientId: string;
  energyType: EnergyType;
  tariffType: TariffType;
  isHeat: boolean;
  onClose: () => void;
  onCreated: () => void;
}) {
  const { t } = useTranslation();
  const today = new Date().toISOString().slice(0, 10);

  const isDynamic = tariffType === "rdn_dynamic" && !isHeat;

  const [form, setForm] = useState<TariffCreate>({
    energy_type: energyType,
    effective_from: today,
    fixed_price: isDynamic ? null : isHeat ? 0 : 9.5,
  });
  const [error, setError] = useState<string | null>(null);

  const create = useMutation({
    mutationFn: () => clientsApi.createTariff(clientId, form),
    onSuccess: () => onCreated(),
    onError: (e: unknown) => {
      const code = (e as { response?: { data?: { error?: { code?: string } } } })?.response?.data?.error?.code;
      setError(
        code === "tariff_conflict"
          ? t("clients.errors.tariff_conflict")
          : t("clients.errors.unknown"),
      );
    },
  });

  return (
    <div className="fixed inset-0 bg-slate-900/40 backdrop-blur-sm flex items-center justify-center p-4 z-50"
      onClick={onClose}>
      <Card className="w-full max-w-md" onClick={(e) => e.stopPropagation()}>
        <CardHeader><CardTitle>{t("clients.add_tariff")}</CardTitle></CardHeader>
        <CardContent className="space-y-3">
          <div className="space-y-1">
            <Label>{t("clients.tariff.effective_from")}</Label>
            <Input type="date" value={form.effective_from}
              onChange={(e) => setForm({ ...form, effective_from: e.target.value })} />
          </div>

          {!isDynamic ? (
            <div className="space-y-1">
              <Label>
                {t("clients.tariff.fixed_price")} (
                {t(`clients.tariff.fixed_price_unit_${energyType}`)})
              </Label>
              <Input type="number" step="0.0001" min={0}
                value={form.fixed_price ?? 0}
                onChange={(e) => setForm({ ...form, fixed_price: Number(e.target.value) })} />
              {isHeat && (
                <p className="text-xs text-slate-500">
                  {t("clients.tariff.fixed_price_hint_heat_zero")}
                </p>
              )}
            </div>
          ) : (
            <div className="text-sm text-slate-500 italic">
              {t("clients.tariff.fixed_price_hint_dynamic")}
            </div>
          )}

          {error && <div className="text-sm text-red-600">{error}</div>}
        </CardContent>
        <div className="flex gap-2 px-6 pb-6">
          <Button onClick={() => create.mutate()} disabled={create.isPending}>
            {create.isPending ? t("common.saving") : t("common.create")}
          </Button>
          <Button variant="outline" onClick={onClose}>{t("common.cancel")}</Button>
        </div>
      </Card>
    </div>
  );
}
